import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { http } from '@/lib/api';
import { useBetterAuth } from '@hooks/useAuth';
import { DEFAULT_MERCHANT_TZ, getMerchantTz, setMerchantTz } from '@/lib/datetime';

interface TimezoneSnapshot {
  timezone: string;
  offset: string;
  abbreviation: string;
  now: string;
}

interface MerchantTimezoneContextValue {
  timezone: string;
  abbreviation: string;
  offset: string;
}

/** Deployment default; served before sign-in. */
const PUBLIC_TIMEZONE_PATH = '/api/public/system/timezone';
/** The session tenant's value; served after sign-in. */
const SESSION_TIMEZONE_PATH = '/api/system/timezone';

const MerchantTimezoneContext = createContext<MerchantTimezoneContextValue>({
  timezone: DEFAULT_MERCHANT_TZ,
  abbreviation: '',
  offset: '',
});

/**
 * Bootstraps the merchant timezone once the session resolves. Before sign-in
 * it reads the deployment default from `/api/public/system/timezone`; after
 * sign-in it reads the session tenant's value from `/api/system/timezone`.
 * It must render inside `BetterAuthProvider`.
 *
 * While the session is still resolving it sends no request: a public read
 * fired first on a signed-in cold load would flash the default zone for a
 * tenant in another zone, and its late response could overwrite the cache.
 * A response whose effect was cancelled (session changed meanwhile) is
 * dropped. Until a request resolves we render with whatever `getMerchantTz()`
 * returns from cache (last-known-good or hardcoded default), so the UI does
 * not flash a stale day before the network round-trip finishes.
 */
export function MerchantTimezoneProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<TimezoneSnapshot>(() => ({
    timezone: getMerchantTz(),
    offset: '',
    abbreviation: '',
    now: new Date().toISOString(),
  }));
  const { user, loading } = useBetterAuth();
  const userId = user?.id ?? null;

  const fetchSnapshot = async (path: string, isCancelled: () => boolean) => {
    try {
      // `http.get` already unwraps the `{ data }` success envelope.
      const data = await http.get<TimezoneSnapshot>(path);
      if (isCancelled()) return;
      if (data?.timezone) {
        // Cache only the session tenant's zone. The public endpoint returns the
        // deployment default, which belongs to no tenant; caching it would
        // overwrite the last signed-in tenant's value.
        if (path === SESSION_TIMEZONE_PATH) setMerchantTz(data.timezone);
        setSnapshot(data);
      }
    } catch (err) {
      // Non-fatal: fall back to cached / default. Operator-facing
      // surfaces still render — just in the previous tz until the
      // next successful fetch.
      console.warn('[MerchantTimezoneContext] bootstrap failed:', err);
    }
  };

  const snapshotPath = userId === null ? PUBLIC_TIMEZONE_PATH : SESSION_TIMEZONE_PATH;

  // Keyed on `userId`, not the path: a switch between two signed-in users
  // (two tenants) keeps the path but must re-read the zone.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    void fetchSnapshot(snapshotPath, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [loading, userId]);

  return (
    <MerchantTimezoneContext.Provider
      value={{
        timezone: snapshot.timezone,
        abbreviation: snapshot.abbreviation,
        offset: snapshot.offset,
      }}
    >
      {children}
    </MerchantTimezoneContext.Provider>
  );
}

export function useMerchantTimezone() {
  return useContext(MerchantTimezoneContext);
}
